import axios from 'axios';

import { getCurrency } from 'helpers/locale';
import { AggregateSaleData } from 'root/apps/QuoteMachineIntegration/state/AggregateSaleData';
import { DataWithMetadata } from 'root/apps/QuoteMachineIntegration/state/DataWithMetadata';
import {
  PaymentRequestFilters,
  PaymentRequestListResponse,
  PaymentRequestPayload,
  PaymentRequestResponse,
  PaymentRequestSearchFilters,
  PaymentRequestSearchResponse,
  PricingTable,
  PricingTableTypes,
} from 'root/apps/QuoteMachineIntegration/state/PaymentRequest';
import {
  buildCustomer,
  buildLocation,
  buildPricingTable,
} from 'root/apps/QuoteMachineIntegration/utils/PaymentRequestBuilders';
import { datadogRum } from 'root/datadog/datadogRum';

const BASE_URL = '/admin/invoicing/payment-requests';
const INTEGRATION_KEY = 'ls-retail';

/**
 * Determines the payment action based on the user's module access
 * @returns 'deposit' for invoicing users, 'payment' for non invoicing users
 */
function getPaymentAction(): 'deposit' {
  return 'deposit';
}

export default class PaymentRequestService {
  static async buildPaymentRequestDto(sale: AggregateSaleData): Promise<PaymentRequestPayload> {
    const paymentAction = getPaymentAction();
    const accountID = window.merchantos.account.id;
    const customer = buildCustomer(sale);
    const defaultCustomerEmail = String(customer?.email ?? '');
    const currency = getCurrency()?.currencyCode;

    const results = await Promise.all([
      buildPricingTable(PricingTableTypes.SALE, sale, accountID),
      buildPricingTable(PricingTableTypes.LAYAWAY, sale, accountID),
      buildPricingTable(PricingTableTypes.SPECIAL_ORDER, sale, accountID),
    ]);

    const allPricingTables = results.filter(
      (table): table is PricingTable => table !== null && table !== undefined
    );

    if (!sale.Sale?.registerID) {
      datadogRum.addError(new Error('Missing registerID'), {
        saleID: sale.Sale?.saleID,
        message: 'Cannot build payment request DTO without registerID',
      });

      throw new Error('Cannot build payment request DTO without registerID');
    }
    return {
      sendToEmail: defaultCustomerEmail,
      paymentAction,
      requestedAmount: 0,
      integrationKey: INTEGRATION_KEY,
      currency,
      location: buildLocation(sale),
      customer,
      note: '',
      documentRefs: [String(sale.Sale?.saleID ?? '')].filter(Boolean),
      registerRef: String(sale.Sale?.registerID ?? ''),
      shopRef: String(sale.Sale?.Shop?.shopID ?? ''),
      employeeRef: String(sale.Sale?.employeeID ?? ''),
      accountRef: String(accountID ?? ''),
      pricingTables: allPricingTables,
    };
  }

  static async buildPaymentRequestDtoForQuote(
    quote: AggregateSaleData,
    quoteID?: string
  ): Promise<PaymentRequestPayload> {
    const paymentAction = getPaymentAction();
    const accountID = window.merchantos.account.id;
    const customer = buildCustomer(quote);
    const defaultCustomerEmail = String(customer?.email ?? '');
    const currency = getCurrency()?.currencyCode;

    const quotePricingTable = await buildPricingTable(PricingTableTypes.QUOTE, quote, accountID);

    if (quoteID) {
      quotePricingTable.title = `Quote #${quoteID}`;
    } else {
      quotePricingTable.title = quote.Sale?.saleID ? `Quote #${quote.Sale.saleID}` : 'Quote';
    }

    if (!quote.Sale?.registerID) {
      datadogRum.addError(new Error('Missing registerID'), {
        saleID: quote.Sale?.saleID,
        message: 'Cannot build quote payment request DTO without registerID',
      });
      throw new Error('Cannot build payment request DTO without registerID');
    }

    const saleID = quote.Sale?.saleID ?? '';
    const documentRefs = [
      saleID ? `quote-${saleID}` : '',
      quoteID ? `hr-quote-${quoteID}` : '',
    ].filter(Boolean);

    return {
      sendToEmail: defaultCustomerEmail,
      paymentAction,
      requestedAmount: 0,
      integrationKey: INTEGRATION_KEY,
      currency,
      location: buildLocation(quote),
      customer,
      note: '',
      documentRefs,
      registerRef: String(quote.Sale?.registerID ?? ''),
      shopRef: String(quote.Sale?.Shop?.shopID ?? ''),
      employeeRef: String(quote.Sale?.employeeID ?? ''),
      accountRef: String(accountID ?? ''),
      pricingTables: [quotePricingTable],
    };
  }

  static async buildPaymentRequestDtoForWorkorder(
    workorder: AggregateSaleData
  ): Promise<PaymentRequestPayload> {
    const paymentAction = getPaymentAction();
    // Use the existing sale builder but add workorder-specific metadata
    const accountID = window.merchantos.account.id;
    const customer = buildCustomer(workorder);
    const defaultCustomerEmail = String(customer?.email ?? '');
    const currency = getCurrency()?.currencyCode;

    const workOrderPricingTable = await Promise.resolve(
      buildPricingTable(PricingTableTypes.WORKORDER, workorder, accountID)
    );

    workOrderPricingTable.title = workorder.Sale?.saleID
      ? `Work Order #${workorder.Sale.saleID}`
      : 'Work Order';

    if (!window.merchantos.session.getRegister()?.registerID) {
      datadogRum.addError(new Error('Missing registerID'), {
        workorderID: workorder.Sale?.saleID,
        message: 'Cannot build workorder payment request DTO without registerID',
      });
      throw new Error('Cannot build payment request DTO without registerID');
    }

    // Add workorder-specific metadata
    return {
      sendToEmail: defaultCustomerEmail,
      paymentAction,
      requestedAmount: 0,
      integrationKey: INTEGRATION_KEY,
      currency,
      location: buildLocation(workorder),
      customer,
      note: '',
      documentRefs: [`work-order-${String(workorder.Sale?.saleID ?? '')}`].filter(Boolean),
      registerRef: String(window.merchantos.session.getRegister()?.registerID),
      shopRef: String(workorder.Sale?.Shop?.shopID ?? ''),
      employeeRef: String(workorder.Sale?.employeeID ?? ''),
      accountRef: String(accountID ?? ''),
      pricingTables: [workOrderPricingTable],
    };
  }

  static async createPaymentRequest(payload: PaymentRequestPayload) {
    try {
      return await axios.post(BASE_URL, payload);
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      datadogRum.addError(err, { message: 'Api call to create payment request failed' });
      throw err;
    }
  }

  static async fetchPaymentRequestsWithFilters(
    filters: PaymentRequestFilters
  ): Promise<PaymentRequestListResponse> {
    try {
      const { data } = await axios.get<PaymentRequestListResponse>(BASE_URL, {
        params: {
          ...filters,
        },
      });
      return data ?? [];
    } catch (error) {
      datadogRum.addError(error as Error, {
        message: 'Failed to fetch /payment-requests',
        filters,
        timeStamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  static async cancelPaymentRequest(id: string) {
    try {
      const { data } = await axios.post(`${BASE_URL}/${id}/cancel`);
      return data;
    } catch (error) {
      datadogRum.addError(error as Error, {
        message: 'Failed to cancel payment request',
        id,
      });
      throw error;
    }
  }

  static async resendPaymentRequest(id: string, sendToEmail: string) {
    try {
      const { data } = await axios.post(`${BASE_URL}/${id}/send`, { sendToEmail });
      return data;
    } catch (error) {
      datadogRum.addError(error as Error, {
        message: 'Failed to resend payment request',
        id,
      });
      throw error;
    }
  }

  static async refreshPaymentRequest(id: string) {
    try {
      const { data } = await axios.post(`${BASE_URL}/${id}/refresh`);
      return data;
    } catch (error) {
      datadogRum.addError(error as Error, {
        message: 'Failed to refresh payment request',
        id,
      });
      throw error;
    }
  }

  static async getPaymentRequestById(id: string): Promise<PaymentRequestResponse> {
    try {
      const { data } = await axios.get<PaymentRequestResponse>(`${BASE_URL}/${id}`);
      return data;
    } catch (error) {
      datadogRum.addError(error as Error, {
        message: 'Failed to fetch payment request by id',
        id,
      });
      throw error;
    }
  }

  // Used to feed payment request listing
  static async searchPaymentRequests(
    filters: PaymentRequestSearchFilters
  ): Promise<PaymentRequestSearchResponse> {
    try {
      const { data: responseBody } = await axios.get<PaymentRequestSearchResponse>(
        `${BASE_URL}/search`,
        {
          params: filters,
        }
      );
      return responseBody;
    } catch (error) {
      datadogRum.addError(error as Error, {
        message: 'Failed to fetch /payment-requests/search',
        filters,
        timeStamp: new Date().toISOString(),
      });
      throw error;
    }
  }

  static async fetchCollectedPaymentRequestsTotal(customerRef: string) {
    try {
      const { data } = await axios.get<DataWithMetadata<any>>(
        `admin/invoicing/payment-request/deposits/collected-total?customerId=${customerRef}&currency=${window.merchantos.locale.currency_code}`
      );
      return data;
    } catch (error) {
      datadogRum.addError(error as Error, {
        message: `Failed to fetch /payment-methods/deposits/collected-total?customerId=${customerRef}`,
        customerRef,
        timeStamp: new Date().toISOString(),
      });
      throw error;
    }
  }
}
